import { useState, useRef, useEffect } from 'react';
import { MdCalendarToday, MdClose } from 'react-icons/md';
import { adToBs } from '../../utils/calendar';
import DualDatePicker from './DualDatePicker';

// A compact date field: a single themed trigger showing the picked date as "BS · AD"
// (or a muted placeholder when empty), which opens the shared DualDatePicker inside a
// popover. It's purely presentational over DualDatePicker — same AD ISO "YYYY-MM-DD"
// value in/out, and '' still means "no date". Theme-aware (semantic tokens, no dark:).

const fmtAd = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
// "Asar 11, 2083 · 25 Jun 2026" — reuses adToBs, no new date lib.
const fmtBsAd = (iso) => {
  try {
    const d = new Date(`${iso}T00:00:00`);
    const bs = adToBs(d);
    return `${bs.monthName} ${bs.day}, ${bs.year} · ${fmtAd(d)}`;
  } catch {
    return iso;
  }
};

function DateField({ value = '', onChange, label = 'Date', placeholder = 'Any date', align = 'left', min, max }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Close on outside-click and Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Forward the picker's value; close once a complete date is chosen, but stay open
  // while a BS date is still being assembled (intermediate value is '').
  const handlePick = (v) => {
    onChange(v);
    if (v) setOpen(false);
  };

  const clear = () => onChange('');

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`w-full flex items-center gap-2 bg-card border rounded-md pl-3 pr-9 py-2 text-sm text-left transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 ${
          open ? 'border-primary' : 'border-divider'
        }`}
      >
        <MdCalendarToday className="text-muted text-base flex-shrink-0" />
        <span className={`flex-1 min-w-0 truncate ${value ? 'text-secondary' : 'text-muted'}`}>
          {value ? fmtBsAd(value) : placeholder}
        </span>
      </button>

      {/* Clear — sibling button (not nested), shown only when a date is set */}
      {value && (
        <button
          type="button"
          onClick={clear}
          title="Clear date"
          aria-label="Clear date"
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded text-muted hover:text-secondary hover:bg-nav-hover transition-colors"
        >
          <MdClose className="text-sm" />
        </button>
      )}

      {open && (
        <div
          role="dialog"
          className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full mt-1 z-50 w-[280px] max-w-[calc(100vw-2rem)] bg-card border border-divider rounded-lg shadow-xl p-3`}
        >
          <DualDatePicker label={label} value={value} onChange={handlePick} min={min} max={max} />
        </div>
      )}
    </div>
  );
}

export default DateField;
